import { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, Clock, Eye, AlertCircle, Download, Trash2, Edit } from 'lucide-react';
import { adminReservationApi } from '../services/api';
import { AdminReservationResponse, AdminReservationListResponse } from '../types';
import { decodeHtmlEntities } from '../utils/htmlDecode';
import { formatCurrency } from '../utils/currency';

export default function AdminReservationManagement() {
  const [reservations, setReservations] = useState<AdminReservationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedReservations, setSelectedReservations] = useState<Set<number>>(new Set());

  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [bulkCancelModalOpen, setBulkCancelModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<AdminReservationResponse | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Update form state
  const [updateStatus, setUpdateStatus] = useState<'pending' | 'confirmed' | 'cancelled'>('pending');
  const [updatePaymentStatus, setUpdatePaymentStatus] = useState<'pending' | 'paid' | 'refunded'>('pending');
  const [updateNotes, setUpdateNotes] = useState('');

  // Cancel form state
  const [cancelReason, setCancelReason] = useState('');

  const pageSize = 20;

  const loadReservations = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response: AdminReservationListResponse = await adminReservationApi.getReservations(
        statusFilter || undefined,
        page,
        pageSize
      );
      setReservations(response.items);
      setTotal(response.total);
      setTotalPages(response.total_pages);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError.response?.data?.detail || 'Failed to load reservations');
      console.error('Error loading reservations:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page]);

  useEffect(() => {
    loadReservations();
  }, [loadReservations]);

  // Clear selected reservations when filters or pagination change
  // This prevents bulk actions on off-screen items from previous pages/filters
  useEffect(() => {
    setSelectedReservations(new Set());
  }, [statusFilter, page]);

  const handleUpdateReservation = async () => {
    if (!selectedReservation) return;
    try {
      setActionLoading(true);
      await adminReservationApi.updateReservation(selectedReservation.id, {
        status: updateStatus,
        payment_status: updatePaymentStatus,
        notes: updateNotes || undefined,
      });
      setUpdateModalOpen(false);
      setSelectedReservation(null);
      await loadReservations();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError.response?.data?.detail || 'Failed to update reservation');
      console.error('Error updating reservation:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelReservation = async () => {
    if (!selectedReservation) return;
    try {
      setActionLoading(true);
      await adminReservationApi.cancelReservation(selectedReservation.id, cancelReason || undefined);
      setCancelModalOpen(false);
      setSelectedReservation(null);
      setCancelReason('');
      await loadReservations();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError.response?.data?.detail || 'Failed to cancel reservation');
      console.error('Error cancelling reservation:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkCancel = async () => {
    try {
      setActionLoading(true);
      const cancelPromises = Array.from(selectedReservations).map(id =>
        adminReservationApi.cancelReservation(id, cancelReason || undefined)
      );
      await Promise.all(cancelPromises);
      setBulkCancelModalOpen(false);
      setSelectedReservations(new Set());
      setCancelReason('');
      await loadReservations();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError.response?.data?.detail || 'Failed to cancel reservations');
      console.error('Error bulk cancelling reservations:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const openViewModal = (reservation: AdminReservationResponse) => {
    setSelectedReservation(reservation);
    setViewModalOpen(true);
  };

  const openUpdateModal = (reservation: AdminReservationResponse) => {
    setSelectedReservation(reservation);
    setUpdateStatus(reservation.status);
    setUpdatePaymentStatus(reservation.payment_status);
    setUpdateNotes(reservation.notes || '');
    setUpdateModalOpen(true);
  };

  const openCancelModal = (reservation: AdminReservationResponse) => {
    setSelectedReservation(reservation);
    setCancelReason('');
    setCancelModalOpen(true);
  };

  const openBulkCancelModal = () => {
    setCancelReason('');
    setBulkCancelModalOpen(true);
  };

  const toggleSelectReservation = (id: number) => {
    const newSelected = new Set(selectedReservations);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedReservations(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedReservations.size === reservations.length) {
      setSelectedReservations(new Set());
    } else {
      setSelectedReservations(new Set(reservations.map(r => r.id)));
    }
  };

  /**
   * Safely escape a CSV cell value to prevent CSV injection and data corruption
   * - Escapes quotes by doubling them
   * - Normalizes newlines to spaces
   * - Prefixes dangerous leading characters (=, +, -, @) with a single quote
   */
  const escapeCsvCell = (value: string | number | undefined): string => {
    if (value === undefined || value === null) return '';

    let cellValue = String(value);

    // Normalize newlines to spaces to prevent multi-line cells
    cellValue = cellValue.replace(/[\r\n]+/g, ' ');

    // CSV injection prevention: prefix dangerous leading characters
    if (/^[=+\-@]/.test(cellValue)) {
      cellValue = "'" + cellValue;
    }

    // Escape double quotes by doubling them (CSV standard)
    cellValue = cellValue.replace(/"/g, '""');

    return `"${cellValue}"`;
  };

  const exportToCSV = () => {
    const headers = [
      'ID',
      'Vendor',
      'Email',
      'Fair',
      'Slot',
      'Status',
      'Payment Status',
      'Price Paid',
      'Reservation Type',
      'Created At',
    ];

    const rows = reservations.map(r => [
      r.id,
      r.vendor_business_name,
      r.vendor_email,
      r.fair_name,
      r.slot_number,
      r.status,
      r.payment_status,
      r.price_paid,
      r.reservation_type,
      r.created_at || '',
    ]);

    const csvContent = [
      headers.map(h => escapeCsvCell(h)).join(','),
      ...rows.map(row => row.map(cell => escapeCsvCell(cell)).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    try {
      link.setAttribute('href', url);
      link.setAttribute('download', `reservations_${new Date().toISOString()}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      // Always revoke the object URL to prevent memory leaks
      URL.revokeObjectURL(url);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Pending' },
      confirmed: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Confirmed' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Cancelled' },
    };
    const badge = badges[status as keyof typeof badges] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="h-3 w-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  const getPaymentBadge = (paymentStatus: string) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      paid: { bg: 'bg-green-100', text: 'text-green-800', label: 'Paid' },
      refunded: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Refunded' },
    };
    const badge = badges[paymentStatus as keyof typeof badges] || badges.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="admin-reservation-management">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="reservations-title">
            Reservation Management
          </h1>
          <p className="text-gray-600">
            View and manage all reservations across the platform
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 rounded-md p-4 flex items-center" data-testid="reservations-error">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Filters and Actions */}
        <div className="mb-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <label htmlFor="status-filter" className="text-sm font-medium text-gray-700">
              Filter by Status:
            </label>
            <select
              id="status-filter"
              data-testid="status-filter-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="flex items-center space-x-3">
            {selectedReservations.size > 0 && (
              <button
                onClick={openBulkCancelModal}
                data-testid="bulk-cancel-button"
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Cancel Selected ({selectedReservations.size})
              </button>
            )}
            <button
              onClick={exportToCSV}
              data-testid="export-csv-button"
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark flex items-center"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </button>
          </div>
        </div>

        <div className="text-sm text-gray-600 mb-4" data-testid="reservations-total">
          Total: {total} reservation{total !== 1 ? 's' : ''}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12" data-testid="reservations-loading">
            <div className="text-gray-600">Loading reservations...</div>
          </div>
        )}

        {/* Empty State */}
        {!loading && reservations.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg shadow" data-testid="reservations-empty">
            <Clock className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Reservations Found</h3>
            <p className="text-gray-600">
              {statusFilter
                ? `No ${statusFilter} reservations at this time.`
                : 'No reservations have been created yet.'}
            </p>
          </div>
        )}

        {/* Desktop Table View */}
        {!loading && reservations.length > 0 && (
          <div className="hidden md:block bg-white shadow-md rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200" data-testid="reservations-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedReservations.size === reservations.length}
                      onChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                      className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vendor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fair
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Slot
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reservations.map((reservation) => (
                  <tr
                    key={reservation.id}
                    data-testid="reservation-row"
                    className="hover:bg-gray-50"
                  >
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedReservations.has(reservation.id)}
                        onChange={() => toggleSelectReservation(reservation.id)}
                        data-testid={`select-reservation-${reservation.id}`}
                        className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{reservation.vendor_business_name}</div>
                      <div className="text-sm text-gray-500">{reservation.vendor_email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {truncateText(reservation.fair_name, 30)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {reservation.slot_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(reservation.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPaymentBadge(reservation.payment_status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(reservation.price_paid)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {reservation.created_at ? new Date(reservation.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => openViewModal(reservation)}
                        data-testid="view-reservation-button"
                        className="text-secondary hover:text-teal-900"
                        title="View details"
                      >
                        <Eye className="h-5 w-5 inline" />
                      </button>
                      {reservation.status !== 'cancelled' && (
                        <>
                          <button
                            onClick={() => openUpdateModal(reservation)}
                            data-testid="update-reservation-button"
                            className="text-secondary hover:text-ink"
                            title="Update reservation"
                          >
                            <Edit className="h-5 w-5 inline" />
                          </button>
                          <button
                            onClick={() => openCancelModal(reservation)}
                            data-testid="cancel-reservation-button"
                            className="text-red-600 hover:text-red-900"
                            title="Cancel reservation"
                          >
                            <Trash2 className="h-5 w-5 inline" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Mobile Card View */}
        {!loading && reservations.length > 0 && (
          <div className="md:hidden space-y-4">
            {reservations.map((reservation) => (
              <div key={reservation.id} className="bg-white rounded-lg shadow p-4">
                <div className="flex items-start justify-between mb-3">
                  <input
                    type="checkbox"
                    checked={selectedReservations.has(reservation.id)}
                    onChange={() => toggleSelectReservation(reservation.id)}
                    className="h-4 w-4 mt-1 text-secondary focus:ring-primary border-gray-300 rounded"
                  />
                  <div className="flex-1 ml-3">
                    <div className="font-medium text-gray-900">{reservation.vendor_business_name}</div>
                    <div className="text-sm text-gray-600">{reservation.vendor_email}</div>
                  </div>
                  <div className="flex space-x-1">
                    {getStatusBadge(reservation.status)}
                    {getPaymentBadge(reservation.payment_status)}
                  </div>
                </div>
                <div className="text-sm text-gray-700 space-y-1 mb-3">
                  <div><strong>Fair:</strong> {reservation.fair_name}</div>
                  <div><strong>Slot:</strong> {reservation.slot_number}</div>
                  <div><strong>Price:</strong> {formatCurrency(reservation.price_paid)}</div>
                  <div><strong>Created:</strong> {reservation.created_at ? new Date(reservation.created_at).toLocaleDateString() : 'N/A'}</div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => openViewModal(reservation)}
                    className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
                  >
                    <Eye className="h-4 w-4 inline mr-1" />
                    View
                  </button>
                  {reservation.status !== 'cancelled' && (
                    <>
                      <button
                        onClick={() => openUpdateModal(reservation)}
                        className="flex-1 px-3 py-2 bg-moonbone text-ink rounded-md hover:bg-fae-blush text-sm"
                      >
                        <Edit className="h-4 w-4 inline mr-1" />
                        Update
                      </button>
                      <button
                        onClick={() => openCancelModal(reservation)}
                        className="flex-1 px-3 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 text-sm"
                      >
                        <Trash2 className="h-4 w-4 inline mr-1" />
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* View Details Modal */}
      {viewModalOpen && selectedReservation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="view-reservation-modal">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Reservation Details</h2>
                <div className="flex space-x-2">
                  {getStatusBadge(selectedReservation.status)}
                  {getPaymentBadge(selectedReservation.payment_status)}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Vendor Information</h3>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Business Name</label>
                      <p className="text-gray-900">{selectedReservation.vendor_business_name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <p className="text-gray-900">{selectedReservation.vendor_email}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Fair & Slot</h3>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Fair Name</label>
                      <p className="text-gray-900">{selectedReservation.fair_name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Slot Number</label>
                      <p className="text-gray-900">{selectedReservation.slot_number}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Reservation Details</h3>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Type</label>
                      <p className="text-gray-900">{selectedReservation.reservation_type === 'full_duration' ? 'Full Duration' : 'Per Day'}</p>
                    </div>
                    {selectedReservation.selected_dates && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Selected Dates</label>
                        <p className="text-gray-900">{selectedReservation.selected_dates.join(', ')}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment Information</h3>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Price Paid</label>
                      <p className="text-gray-900">{formatCurrency(selectedReservation.price_paid)}</p>
                    </div>
                    {selectedReservation.payment_method && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Payment Method</label>
                        <p className="text-gray-900">{selectedReservation.payment_method}</p>
                      </div>
                    )}
                    {selectedReservation.stripe_payment_intent_id && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Stripe Payment Intent</label>
                        <p className="text-gray-900 text-xs font-mono">{selectedReservation.stripe_payment_intent_id}</p>
                      </div>
                    )}
                  </div>
                </div>

                {(selectedReservation.platform_commission_rate || selectedReservation.platform_commission_amount || selectedReservation.fair_admin_payout_amount) && (
                  <div className="col-span-2">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Commission & Payout</h3>
                    <div className="grid grid-cols-3 gap-4">
                      {selectedReservation.platform_commission_rate && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Commission Rate</label>
                          <p className="text-gray-900">{selectedReservation.platform_commission_rate}%</p>
                        </div>
                      )}
                      {selectedReservation.platform_commission_amount && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Commission Amount</label>
                          <p className="text-gray-900">${selectedReservation.platform_commission_amount.toFixed(2)}</p>
                        </div>
                      )}
                      {selectedReservation.fair_admin_payout_amount && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Fair Admin Payout</label>
                          <p className="text-gray-900">${selectedReservation.fair_admin_payout_amount.toFixed(2)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedReservation.notes && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <p className="text-gray-900 whitespace-pre-wrap">{decodeHtmlEntities(selectedReservation.notes)}</p>
                  </div>
                )}

                <div className="col-span-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Created At</label>
                      <p className="text-gray-900">{selectedReservation.created_at ? new Date(selectedReservation.created_at).toLocaleString() : 'N/A'}</p>
                    </div>
                    {selectedReservation.updated_at && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Updated At</label>
                        <p className="text-gray-900">{new Date(selectedReservation.updated_at).toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                {selectedReservation.status !== 'cancelled' && (
                  <>
                    <button
                      onClick={() => {
                        setViewModalOpen(false);
                        openUpdateModal(selectedReservation);
                      }}
                      className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
                    >
                      <Edit className="h-4 w-4 inline mr-1" />
                      Update
                    </button>
                    <button
                      onClick={() => {
                        setViewModalOpen(false);
                        openCancelModal(selectedReservation);
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                    >
                      <Trash2 className="h-4 w-4 inline mr-1" />
                      Cancel
                    </button>
                  </>
                )}
                <button
                  onClick={() => setViewModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update Reservation Modal */}
      {updateModalOpen && selectedReservation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="update-reservation-modal">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Update Reservation</h2>
            <p className="text-gray-700 mb-4">
              Update reservation for <strong>{selectedReservation.vendor_business_name}</strong>
            </p>
            <div className="space-y-4">
              <div>
                <label htmlFor="update-status" className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  id="update-status"
                  data-testid="update-status-select"
                  value={updateStatus}
                  onChange={(e) => setUpdateStatus(e.target.value as 'pending' | 'confirmed' | 'cancelled')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label htmlFor="update-payment-status" className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Status
                </label>
                <select
                  id="update-payment-status"
                  data-testid="update-payment-status-select"
                  value={updatePaymentStatus}
                  onChange={(e) => setUpdatePaymentStatus(e.target.value as 'pending' | 'paid' | 'refunded')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                >
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="refunded">Refunded</option>
                </select>
              </div>
              <div>
                <label htmlFor="update-notes" className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  id="update-notes"
                  data-testid="update-notes-textarea"
                  value={updateNotes}
                  onChange={(e) => setUpdateNotes(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  placeholder="Add notes about this update"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setUpdateModalOpen(false)}
                disabled={actionLoading}
                data-testid="cancel-update-button"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateReservation}
                disabled={actionLoading}
                data-testid="confirm-update-button"
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
              >
                {actionLoading ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Reservation Modal */}
      {cancelModalOpen && selectedReservation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="cancel-reservation-modal">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Cancel Reservation</h2>
            <p className="text-gray-700 mb-4">
              Are you sure you want to cancel this reservation for <strong>{selectedReservation.vendor_business_name}</strong>?
            </p>
            <div className="mb-6">
              <label htmlFor="cancel-reason" className="block text-sm font-medium text-gray-700 mb-1">
                Cancellation Reason (optional)
              </label>
              <textarea
                id="cancel-reason"
                data-testid="cancel-reason-textarea"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                placeholder="Reason for cancellation"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setCancelModalOpen(false)}
                disabled={actionLoading}
                data-testid="cancel-cancel-button"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Back
              </button>
              <button
                onClick={handleCancelReservation}
                disabled={actionLoading}
                data-testid="confirm-cancel-button"
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading ? 'Cancelling...' : 'Cancel Reservation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Cancel Modal */}
      {bulkCancelModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="bulk-cancel-modal">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Bulk Cancel Reservations</h2>
            <p className="text-gray-700 mb-4">
              Are you sure you want to cancel <strong>{selectedReservations.size} reservation(s)</strong>?
            </p>
            <div className="mb-6">
              <label htmlFor="bulk-cancel-reason" className="block text-sm font-medium text-gray-700 mb-1">
                Cancellation Reason (optional)
              </label>
              <textarea
                id="bulk-cancel-reason"
                data-testid="bulk-cancel-reason-textarea"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                placeholder="Reason for cancellation"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setBulkCancelModalOpen(false)}
                disabled={actionLoading}
                data-testid="cancel-bulk-cancel-button"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Back
              </button>
              <button
                onClick={handleBulkCancel}
                disabled={actionLoading}
                data-testid="confirm-bulk-cancel-button"
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading ? 'Cancelling...' : 'Cancel Reservations'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
